import { useState, useRef, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import readNDJSONStream from "ndjson-readablestream";
import { chatV2StreamApi, Domain, V2ChatRequest, V2Delta, V2ChatHistoryApiResponse, V2ChatHistoryTurn } from "../api";
import { getToken } from "../authConfig";
import { useLogin } from "../authConfig";
import { ChatStreamError } from "../utils/chatError";

type V2Accumulator = Omit<V2Delta, "response"> & { response: string };

export const useChatApi = () => {
    const STREAM_RENDER_INTERVAL_MS = 33;

    const [isLoading, setIsLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<unknown>();
    const [parsedAnswers, setParsedAnswers] = useState<V2ChatHistoryTurn[]>([]);
    const [parsedStreamedAnswer, setParsedStreamedAnswer] = useState<V2ChatHistoryTurn | undefined>(undefined);
    const [conversationId, setConversationId] = useState<string | null>(null);

    const client = useLogin ? useMsal().instance : undefined;

    const pendingRenderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const conversationIdRef = useRef<string | null>(null);
    // Incremented whenever a new request starts or the conversation changes (new chat,
    // history item selected). Async continuations of a request (stream loop, render
    // timers, post-await state commits) capture the generation they were started with
    // and must not write any state once a newer generation exists, so an aborted or
    // superseded request cannot leak its data into the next conversation.
    const generationRef = useRef<number>(0);

    const handleOnHistoryItemClick = async (item: V2ChatHistoryApiResponse) => {
        generationRef.current++;
        conversationIdRef.current = item.id;
        setConversationId(item.id);
        setParsedAnswers(item.answers);
        setParsedStreamedAnswer(undefined);
        setIsLoading(false);
        setIsStreaming(false);
    };

    const handleV2AsyncRequest = useCallback(
        async (question: string, domain: Domain, responseBody: ReadableStream<any>, signal: AbortSignal, generation: number): Promise<V2Delta> => {
            if (generationRef.current === generation) {
                setParsedStreamedAnswer({ userMessage: question, response: { response: "" }, domain });
            }

            if (pendingRenderTimerRef.current) {
                clearTimeout(pendingRenderTimerRef.current);
                pendingRenderTimerRef.current = null;
            }

            const acc: V2Accumulator = {
                id: null,
                response: "",
                sources: [],
                thoughts: [],
                followUpQuestions: [],
                toolCalls: [],
                phiDetected: false
            };

            const flushStreaming = () => {
                if (generationRef.current !== generation) {
                    return;
                }
                setIsLoading(false);
                setParsedStreamedAnswer({ userMessage: question, response: { ...acc, phiDetected: false }, domain });
            };

            // Throttle rendering with a trailing timer instead of awaiting renders in
            // the read loop: the loop only mutates `acc`, so consuming the network
            // stream is never blocked on React updates.
            const scheduleFlush = () => {
                if (pendingRenderTimerRef.current || generationRef.current !== generation) {
                    return;
                }
                pendingRenderTimerRef.current = setTimeout(() => {
                    pendingRenderTimerRef.current = null;
                    flushStreaming();
                }, STREAM_RENDER_INTERVAL_MS);
            };

            try {
                if (generationRef.current === generation) {
                    setIsStreaming(true);
                }
                for await (const delta of readNDJSONStream(responseBody) as AsyncIterable<V2Delta>) {
                    if (signal.aborted || generationRef.current !== generation) break;

                    if (delta.id && !acc.id) {
                        acc.id = delta.id;
                    }

                    if (delta.error) {
                        throw new ChatStreamError(delta.error, delta.errorDetails?.code);
                    }

                    if (delta.toolCalls) {
                        acc.toolCalls = delta.toolCalls;
                        scheduleFlush();
                    }

                    if (delta.phiDetected) {
                        acc.phiDetected = true;
                    }

                    if (delta.response) {
                        acc.response += delta.response;
                        scheduleFlush();
                    }

                    if (delta.sources) acc.sources = delta.sources;
                    if (delta.thoughts) acc.thoughts = delta.thoughts;
                    acc.followUpQuestions = delta.followUpQuestions ?? [];
                }
            } catch (e) {
                if (e instanceof DOMException && e.name === "AbortError") {
                    console.log("Stream aborted by user");
                } else {
                    throw e;
                }
            } finally {
                // A newer generation owns the shared timer ref and status flags by now;
                // a stale request must not touch them.
                if (generationRef.current === generation) {
                    if (pendingRenderTimerRef.current) {
                        clearTimeout(pendingRenderTimerRef.current);
                        pendingRenderTimerRef.current = null;
                    }
                    setIsStreaming(false);
                }
            }

            if (acc.id && generationRef.current === generation) {
                conversationIdRef.current = acc.id;
                setConversationId(acc.id);
            }

            return { ...acc, id: acc.id ?? "" };
        },
        []
    );

    const makeApiRequest = async (
        question: string,
        selectedAssetTypes: string[],
        selectedInterestAreas: string[],
        selectedCountry: string,
        selectedDomain: Domain,
        sstRequestIds: string[],
        signal: AbortSignal
    ) => {
        const generation = ++generationRef.current;
        setError(undefined);
        setIsLoading(true);
        const token = client ? await getToken(client) : undefined;
        try {
            const request: V2ChatRequest = {
                message: question,
                selectedCountries: [selectedCountry],
                domain: selectedDomain,
                userFilters: {
                    assetTypes: selectedAssetTypes,
                    interestAreas: selectedInterestAreas
                },
                id: conversationIdRef.current ?? undefined,
                sstRequestIds
            };

            if (generationRef.current === generation) {
                setParsedStreamedAnswer({ userMessage: question, response: { response: "" }, domain: selectedDomain });
            }

            const response = await chatV2StreamApi(request, token, signal);
            if (!response.body) {
                throw new Error("No response body");
            }
            if (response.status > 299 || !response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const finalResponse = await handleV2AsyncRequest(question, selectedDomain, response.body, signal, generation);
            if (generationRef.current === generation) {
                setParsedAnswers(prev => [...prev, { userMessage: question, response: finalResponse, domain: selectedDomain }]);
                setParsedStreamedAnswer(undefined);
            }
        } catch (e) {
            console.error("Error making API request:", e);
            if (generationRef.current === generation) {
                setError(e);
            }
        } finally {
            if (generationRef.current === generation) {
                setIsLoading(false);
            }
        }
    };

    const clearChat = () => {
        // Invalidate any in-flight request and its pending render timers so they
        // cannot write stale data into the new conversation.
        generationRef.current++;
        if (pendingRenderTimerRef.current) {
            clearTimeout(pendingRenderTimerRef.current);
            pendingRenderTimerRef.current = null;
        }
        error && setError(undefined);
        setParsedAnswers([]);
        setParsedStreamedAnswer(undefined);
        conversationIdRef.current = null;
        setConversationId(null);
        setIsLoading(false);
        setIsStreaming(false);
    };

    return {
        isLoading,
        isStreaming,
        error,
        conversationId,
        parsedStreamedAnswer,
        parsedAnswers,
        makeApiRequest,
        clearChat,
        handleOnHistoryItemClick
    };
};
